// AMEEX delivery card — sits on AdminOrderDetailPage between PDF and Info.
//
// Three states:
//   1. No parcel yet → "Créer le colis" form (city + COD prefilled from order)
//      OR paste an existing parcel code if admin made it in AMEEX directly.
//   2. Parcel exists → status badge, last event, comment, event timeline,
//      Edit + Cancel buttons.
//   3. Parcel returned → extra "Reçu en stock" toggle for the returns shelf.
//
// City id is a free integer input until the cities catalogue ships (the
// AMEEX dashboard's list will be scraped once and dropped in as static
// JSON). We keep last-used city ids on the order itself, so editing an
// existing parcel doesn't require typing the id again.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ameexApi,
  ordersApi,
  settingsApi,
  uploadToSignedUrl,
  type AmeexCanonicalStatus,
  type AmeexStatusEvent,
  type Order,
  ameexPrintApi,
} from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { AmeexCitySelect } from '@/components/admin/AmeexCitySelect';
import { AmeexErrorDialog, toAmeexFailure, type AmeexFailureInfo } from '@/components/admin/AmeexErrorDialog';
import { detectCityFromAddress } from '@/lib/ameexCityMatch';
import { formatDate } from '@/lib/utils';
import { amountReceived, cashToCollect } from '@/lib/cashToCollect';
import { useT } from '@/lib/i18n';

const STATUS_LABELS: Record<AmeexCanonicalStatus, string> = {
  pending:      'En attente',
  in_progress:  'En traitement',
  distribution: 'En distribution',
  delivered:    'Livré',
  postponed:    'Reporté',
  returned:     'Retourné',
  refused:      'Refusé',
  cancelled:    'Annulé',
  refunded:     'Remboursé',
  unresolved:   'À résoudre',
};

// Brand-aligned status palette: soft backgrounds, single accent per state.
const STATUS_TONE: Record<AmeexCanonicalStatus, { bg: string; text: string; dot: string }> = {
  pending:      { bg: 'bg-amber-50',   text: 'text-amber-800',   dot: 'bg-amber-400' },
  in_progress:  { bg: 'bg-blue-50',    text: 'text-blue-800',    dot: 'bg-blue-400' },
  distribution: { bg: 'bg-sky-50',     text: 'text-sky-800',     dot: 'bg-sky-500' },
  delivered:    { bg: 'bg-green-50',   text: 'text-green-800',   dot: 'bg-green-500' },
  postponed:    { bg: 'bg-orange-50',  text: 'text-orange-800',  dot: 'bg-orange-400' },
  returned:     { bg: 'bg-rose-50',    text: 'text-rose-800',    dot: 'bg-rose-500' },
  refused:      { bg: 'bg-red-50',     text: 'text-red-800',     dot: 'bg-red-500' },
  cancelled:    { bg: 'bg-gray-100',   text: 'text-gray-700',    dot: 'bg-gray-400' },
  refunded:     { bg: 'bg-purple-50',  text: 'text-purple-800',  dot: 'bg-purple-400' },
  unresolved:   { bg: 'bg-yellow-50',  text: 'text-yellow-900',  dot: 'bg-yellow-500' },
};

export function AmeexDeliveryCard({
  order,
  shopifyOrderId,
}: {
  order: Order;
  shopifyOrderId: string;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
    queryClient.invalidateQueries({ queryKey: ['ameex-events', shopifyOrderId] });
  };

  const hasParcel = !!order.ameex_parcel_code;
  const status = (order.ameex_status as AmeexCanonicalStatus | null) ?? null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center bg-[linear-gradient(135deg,#F3E6BC_0%,#C9A24A_100%)] dark:bg-[linear-gradient(135deg,#6E561E_0%,#C9A24A_100%)]"
          >
            <span className="text-white text-sm">🚚</span>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-800">{t('Livraison AMEEX')}</h2>
            <p className="text-xs text-gray-500">
              {hasParcel ? t('Colis {code}', { code: order.ameex_parcel_code ?? '' }) : t('Aucun colis créé')}
            </p>
          </div>
        </div>
        {status && (
          <span
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${STATUS_TONE[status].bg} ${STATUS_TONE[status].text}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_TONE[status].dot}`} />
            {t(STATUS_LABELS[status])}
          </span>
        )}
      </header>

      {/* Payment method badge — drives whether an acompte is expected and
          the default state of the advance toggle below. Hidden if the
          customer hasn't confirmed payment method yet (e.g. order created
          via admin /manual route, or still pending checkout). */}
      {order.payment_method && (
        <PaymentMethodBadge method={order.payment_method} />
      )}

      {hasParcel ? (
        <ExistingParcel order={order} shopifyOrderId={shopifyOrderId} onChange={invalidate} />
      ) : (
        <NoParcel order={order} shopifyOrderId={shopifyOrderId} onChange={invalidate} />
      )}
    </div>
  );
}

function PaymentMethodBadge({ method }: { method: 'cod' | 'virement' }) {
  const { t } = useT();
  // Visual hierarchy mirrors what the admin actually needs to act on:
  // virement = orange, "verify the acompte was received before shipping".
  // cod = grey-blue, "the acompte by transfer first, AMEEX collects the rest".
  // ⚠️ 2026-09-29, owner « 1a »: every client pays at least the advance, cash
  // on delivery included — the COD line used to say « Pas d’acompte attendu ».
  if (method === 'virement') {
    return (
      <div className="flex items-start gap-3 rounded-lg p-3 bg-amber-50 border border-amber-200">
        <span className="text-base shrink-0">🏦</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-amber-900">
            {t('Méthode de paiement : Virement bancaire')}
          </p>
          <p className="text-[11px] text-amber-800 mt-0.5 leading-relaxed">
            {t('Le client doit avoir payé l’acompte par virement avant l’expédition. Vérifiez la réception avant de créer le colis — le COD AMEEX sera réduit du montant de l’acompte.')}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3 rounded-lg p-3 bg-slate-50 border border-slate-200">
      <span className="text-base shrink-0">💵</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-800">
          {t('Méthode de paiement : Paiement à la livraison')}
        </p>
        <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">
          {t('L’acompte se paie d’abord par virement ; AMEEX encaisse le reste à la livraison.')}
        </p>
      </div>
    </div>
  );
}

// ─── No-parcel state: Create OR Attach manual ───────────────────────────────
function NoParcel({
  order,
  shopifyOrderId,
  onChange,
}: {
  order: Order;
  shopifyOrderId: string;
  onChange: () => void;
}) {
  const { t } = useT();
  const [mode, setMode] = useState<'create' | 'manual'>('create');
  const initialAddress = extractAddress(order.customer_note) ?? '';
  // Prefill from the city already saved on the order so a previously-picked
  // city survives reloads. Falls back to '' → the address auto-detect below.
  const [cityId, setCityId] = useState<string>(order.ameex_city_id != null ? String(order.ameex_city_id) : '');
  const [cityAutoDetected, setCityAutoDetected] = useState<{ id: number; name: string } | null>(null);
  const [address, setAddress] = useState<string>(initialAddress);
  const [comment, setComment] = useState<string>('');
  const [parcelCode, setParcelCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  // Structured parcel-creation failure → popup. Kept separate from `error`
  // (the small inline line) because a failed shipment must not be missable.
  const [failure, setFailure] = useState<AmeexFailureInfo | null>(null);
  // Set only when the WORKER refuses for a customer-requested delivery date.
  // Kept in state rather than read from the order so the escape hatch
  // appears exactly once somebody has hit the rule, never before.
  const [heldUntil, setHeldUntil] = useState<string | null>(null);

  // Auto-detect city the FIRST time we see an address. Won't clobber an
  // admin manual pick afterwards.
  useEffect(() => {
    if (cityId) return;
    const detected = detectCityFromAddress(address);
    if (detected) {
      setCityId(String(detected.id));
      setCityAutoDetected(detected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  // Pull the admin-configurable defaults: the AMEEX-specific toggles + product
  // label come from the AMEEX section in /admin/settings (Phase 1 of the
  // auto-fill feature).
  const { data: settingsData } = useQuery({
    queryKey: ['app-settings'],
    queryFn: () => settingsApi.get(),
  });
  // ⚠️ THE ACOMPTE STARTS FROM WHAT SHE ACTUALLY PAID (money bug 3, 2026-09-29).
  // It used to start from app_settings.advance_amount × albums (50 per album),
  // ticked « déjà reçu », for every order — a COD client who paid nothing had
  // 50 MAD per album left uncollected unless somebody noticed. Now: the money
  // recorded on the order (an accepted receipt, or the acompte saved here),
  // else 0 and the box unticked. The owner's rule: total − what was actually
  // received; the full total when nothing was. See lib/cashToCollect.ts.
  const received = amountReceived(order);
  const defaultProduct   = (settingsData?.settings?.ameex_default_product as string | undefined) ?? 'Album photo personnalisé';
  const defaultFragile   = (settingsData?.settings?.ameex_default_fragile as boolean | undefined) ?? true;
  const defaultOpen      = (settingsData?.settings?.ameex_default_open    as boolean | undefined) ?? false;
  const defaultTry       = (settingsData?.settings?.ameex_default_try     as boolean | undefined) ?? false;

  // Per-order overrides for the AMEEX form fields. Each defaults to its
  // app_settings counterpart but the admin can edit any of them before
  // hitting Créer le colis without going back to /admin/settings.
  // The acompte starts from the money recorded on the order (`received`,
  // above). A value saved here with Enregistrer IS recorded (advance_amount),
  // so the field shows it again on the next render/reload.
  const [advanceAmountOverride, setAdvanceAmountOverride] = useState<string>(String(Math.round(received)));
  // CRITICAL: `Number(x) || fallback` falls back when x === 0, which means
  // typing "0" in the Acompte field silently reverted to the default and saved
  // that instead. Use an explicit finite-number check so 0 sticks.
  const advanceAmount = (() => {
    const trimmed = advanceAmountOverride.trim();
    if (trimmed === '') return Math.round(received);
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : Math.round(received);
  })();
  // Paid-in-full state — admin toggles this when the customer settled the
  // full total via virement. Flipping it ON auto-fills the advance to the
  // total so COD = 0 on the AMEEX label.
  const total = Math.round(Number(order.total_price ?? 0));
  const [paidInFull, setPaidInFull] = useState<boolean>(!!order.paid_in_full);
  // Re-sync the paid-in-full toggle if the parent reloads with a fresh order
  // row (the paidInFull effect below still forces advance = total when the
  // toggle is on).
  useEffect(() => {
    setPaidInFull(!!order.paid_in_full);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.paid_in_full]);
  const [productOverride, setProductOverride] = useState<string>(defaultProduct);
  const [fragileOverride, setFragileOverride] = useState<boolean>(defaultFragile);
  const [openOverride, setOpenOverride]       = useState<boolean>(defaultOpen);
  const [tryOverride, setTryOverride]         = useState<boolean>(defaultTry);

  // Resync overrides when settings finish loading (the initial mount may
  // have used the fallbacks before the GET /settings response landed).
  // Settings no longer drive the acompte (money bug 3) — only the AMEEX
  // product label and toggles — so a late settings response can never snap
  // the acompte back to a default the client never paid.
  useEffect(() => {
    if (!settingsData?.settings) return;
    setProductOverride(defaultProduct);
    setFragileOverride(defaultFragile);
    setOpenOverride(defaultOpen);
    setTryOverride(defaultTry);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsData?.settings]);

  // « Acompte déjà reçu » — ticked only when money is recorded on the order.
  // It used to be ticked for EVERY order, on the old assumption that everyone
  // pre-pays 50 MAD per album; the owner's COD clients were sold « paiement à
  // la livraison » and pay nothing before the courier.
  const [advanceReceived, setAdvanceReceived] = useState<boolean>(received > 0);

  // The COD field is computed by the one rule — total − what was actually
  // received, 0 when paid in full (lib/cashToCollect.ts, mirrored from the
  // worker). Admin can still type-override the field directly (e.g. for a
  // promo or a partial payment).
  const computedCod = cashToCollect({
    total, paid_in_full: paidInFull,
    advance_received_amount: advanceReceived ? advanceAmount : 0,
  });
  const [cod, setCod] = useState<string>(String(computedCod));
  // Reset COD whenever the toggle flips OR settings finish loading. We
  // track the last computed value to detect manual edits and avoid
  // clobbering them on re-renders.
  const lastComputedCodRef = useRef<number>(computedCod);
  useEffect(() => {
    // Only re-write the COD field if the admin hasn't manually edited it
    // away from the last auto-computed value.
    if (String(lastComputedCodRef.current) === cod) {
      setCod(String(computedCod));
      lastComputedCodRef.current = computedCod;
    } else {
      lastComputedCodRef.current = computedCod;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedCod]);

  const create = useMutation({
    mutationFn: (force?: boolean) =>
      ameexApi.createParcel(shopifyOrderId, {
        ...(force ? { force: true } : {}),
        city_id: Number(cityId),
        address_override: address,
        // Only override the COD when the admin actually typed a value away from
        // the auto-computed one. When it still equals the auto value we send
        // nothing, so the worker computes the COD from the LIVE total itself —
        // that way a stale field (e.g. the live price query hadn't resolved
        // yet) can never push a too-low COD to AMEEX.
        cod_override: Number(cod) === computedCod ? undefined : Number(cod),
        // ⚠️ WHAT THE FORM SHOWS IS WHAT IS SENT (money bug 3, 2026-09-29).
        // This sent the acompte even with « déjà reçu » unticked, so the worker
        // subtracted money the form had just said was NOT received. Unticked
        // sends 0; « payé intégralement » sends the total (COD 0).
        advance_override: paidInFull ? total : (advanceReceived ? advanceAmount : 0),
        comment,
        product_override: productOverride,
        fragile_override: fragileOverride,
        open_override:    openOverride,
        try_override:     tryOverride,
      }),
    // Belt-and-braces (2026-07-29): the worker now 502s when AMEEX returns no
    // parcel code, but if a success ever comes back without one, treat it as a
    // failure here too rather than silently showing "created".
    onSuccess: (res: { parcel_code?: string | null } | undefined) => {
      if (res && !res.parcel_code) {
        setFailure({
          reason: t('AMEEX n’a renvoyé aucun numéro de colis. Le colis n’a PAS été créé.'),
          action: t('Vérifiez la ville et l’adresse, puis réessayez.'),
        });
        return;
      }
      setError(null);
      setFailure(null);
      onChange();
    },
    onError: (e: Error) => {
      // A 409 carrying the customer's requested date is not a failure to fix —
      // it is the hold doing its job. Surface it as such and offer the way
      // past, rather than sending the operator hunting for a bad field.
      const m = /(\d{4}-\d{2}-\d{2})/.exec(e.message ?? '');
      if (/attente|held_until/i.test(e.message ?? '') && m) setHeldUntil(m[1]);
      setFailure(toAmeexFailure(e));
    },
  });
  // When "Payé intégralement par virement" flips ON, mirror it into the
  // advance field so COD = 0 on the AMEEX label. Flipping it OFF restores
  // whatever was last entered (or the global default).
  useEffect(() => {
    if (paidInFull) {
      setAdvanceAmountOverride(String(total));
    }
    // `total` is a dep so that when the live price resolves (or a regen raises
    // the total) the "paid in full" advance tracks it and COD stays 0 instead
    // of leaving a stale gap = (newTotal - oldTotal) to collect at the door.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidInFull, total]);

  // Persist advance_amount + paid_in_full on the order. Decoupled from the
  // parcel creation so admin can save their edits without immediately
  // calling AMEEX. After save we ask the parent to refetch so the order
  // row reflects the new values everywhere (badges, dashboard, etc.).
  const savePricing = useMutation({
    mutationFn: () =>
      ordersApi.updatePricing(shopifyOrderId, {
        advance_amount: advanceAmount,
        paid_in_full: paidInFull,
      }),
    onSuccess: () => { setError(null); onChange(); },
    onError: (e: Error) => setError(e.message),
  });

  // Persist a picked delivery city to the order immediately so it survives
  // reloads even before a parcel is created (the address already lives on the
  // order; the city now does too).
  const saveCity = useMutation({
    mutationFn: (id: number) => ordersApi.updateCustomerInfo(shopifyOrderId, { ameex_city_id: id }),
    onSuccess: () => onChange(),
    onError: (e: Error) => setError(e.message),
  });

  const attach = useMutation({
    mutationFn: () =>
      ameexApi.attachManual(shopifyOrderId, parcelCode.trim(), cityId ? Number(cityId) : undefined),
    onSuccess: () => { setError(null); onChange(); },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="space-y-3">
      <div className="flex gap-1 text-xs bg-gray-50 rounded-lg p-1 w-fit">
        <button
          onClick={() => setMode('create')}
          className={`px-3 py-1.5 rounded-md font-medium transition-colors ${mode === 'create' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'}`}
        >
          {t('Créer via API')}
        </button>
        <button
          onClick={() => setMode('manual')}
          className={`px-3 py-1.5 rounded-md font-medium transition-colors ${mode === 'manual' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'}`}
        >
          {t('Coller un code existant')}
        </button>
      </div>

      {mode === 'create' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field
            label={t('Ville AMEEX *')}
            hint={
              cityAutoDetected
                ? t('✓ Détectée depuis l’adresse : {name} (id {id})', { name: cityAutoDetected.name, id: cityAutoDetected.id })
                : t('Tapez pour rechercher (id envoyé à AMEEX)')
            }
          >
            <AmeexCitySelect
              value={cityId}
              onChange={({ id }) => { setCityId(id); setCityAutoDetected(null); if (id) saveCity.mutate(Number(id)); }}
            />
          </Field>
          <Field
            label={t('COD (MAD) *')}
            hint={
              advanceReceived
                ? t('Total {total} − acompte {advance} = {cod} à encaisser', { total, advance: advanceAmount, cod: computedCod })
                : t('Prix commande: {total} MAD', { total })
            }
          >
            <div className="space-y-1.5">
              <input
                type="number"
                value={cod}
                onChange={(e) => setCod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <label className="flex items-center gap-2 text-[11px] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={advanceReceived}
                  onChange={(e) => setAdvanceReceived(e.target.checked)}
                  className="rounded"
                />
                <span className="text-gray-700">
                  {t('Acompte de {advance} MAD déjà reçu (déduit du COD)', { advance: advanceAmount })}
                </span>
              </label>
              {order.payment_method === 'virement' && !advanceReceived && (
                <p className="text-[11px] text-amber-700">
                  ⚠ {t('Le client a choisi virement bancaire — vérifiez que l’acompte est bien reçu avant d’expédier.')}
                </p>
              )}
            </div>
          </Field>
          <Field label={t('Adresse complète *')} className="md:col-span-2">
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </Field>

          {/* Proof of advance — ALWAYS on the create form (2026-09-29). It is
              where a person pastes the client's slip BEFORE recording the
              amount: it rendered only with « Acompte … déjà reçu » ticked, and
              that box starts unticked when no money is recorded (money bug 3),
              so on a cash-on-delivery order the owner could not find where to
              paste a receipt. Every client pays at least the advance now. */}
          <div className="md:col-span-2">
            <AdvanceProofUploader order={order} shopifyOrderId={shopifyOrderId} onChange={onChange} />
          </div>
          <Field label={t('Nature de produit')} hint={t('Pré-rempli depuis Paramètres → AMEEX')} className="md:col-span-2">
            <input
              type="text"
              value={productOverride}
              onChange={(e) => setProductOverride(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </Field>
          <Field label={t('Commentaire (optionnel)')} className="md:col-span-2">
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder={t('Instructions livreur, étage…')}
            />
          </Field>

          {/* AMEEX toggles — defaults from app_settings, editable per order. */}
          <div className="md:col-span-2 grid sm:grid-cols-3 gap-3 pt-1">
            <ToggleRow label={t('Fragile')}        value={fragileOverride} onChange={setFragileOverride} />
            <ToggleRow label={t('Autoriser l’ouverture')}  value={openOverride}    onChange={setOpenOverride} />
            <ToggleRow label={t('Autoriser l’essayage')}   value={tryOverride}     onChange={setTryOverride} />
          </div>

          {/* Paid-in-full pill (Fix: customer paid the whole total by transfer)
              — ticking ON auto-fills the acompte to the total so COD = 0 on
              the AMEEX label. Save persists both values to the order so the
              dashboard / order list show the paid badge. */}
          <div className="md:col-span-2">
            <label
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                paidInFull
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={paidInFull}
                onChange={(e) => setPaidInFull(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
              />
              <div className="flex-1 text-sm">
                <div className={`font-semibold ${paidInFull ? 'text-green-800' : 'text-gray-800'}`}>
                  {t('Payé intégralement par virement')}
                </div>
                <div className={`text-xs mt-0.5 ${paidInFull ? 'text-green-700' : 'text-gray-500'}`}>
                  {paidInFull
                    ? t('Acompte = {total} MAD · COD = 0 · le badge « Payé » s’affichera après Enregistrer.', { total })
                    : t('Cocher si le client a réglé la totalité (acompte = total, COD = 0).')}
                </div>
              </div>
            </label>
          </div>

          <Field
            label={t('Acompte client (MAD)')}
            hint={paidInFull
              ? t('Verrouillé sur le total tant que « Payé intégralement » est coché.')
              : t('L’argent déjà reçu sur cette commande (0 si rien). Modifiable pour cette commande.')}
            className="md:col-span-2"
          >
            <input
              type="number"
              min={0}
              value={advanceAmountOverride}
              onChange={(e) => setAdvanceAmountOverride(e.target.value)}
              disabled={paidInFull}
              className="w-full sm:w-48 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50 disabled:text-gray-500"
            />
          </Field>

          <div className="md:col-span-2 flex flex-wrap gap-2 justify-end items-center">
            {/* Status hint */}
            {order.paid_in_full && (
              <span className="mr-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                {t('Payé · {date}', { date: order.paid_at ? formatDate(order.paid_at) : '' })}
              </span>
            )}
            <Button
              variant="secondary"
              onClick={() => savePricing.mutate()}
              loading={savePricing.isPending}
              disabled={savePricing.isPending}
            >
              {t('Enregistrer')}
            </Button>
            <Button
              onClick={() => create.mutate(undefined)}
              loading={create.isPending}
              disabled={!cityId || !address.trim() || !cod}
            >
              {t('Créer le colis AMEEX')}
            </Button>
            {/* Only offered once the worker has actually REFUSED for a hold.
                Showing it before that would invite skipping a date nobody
                had asked to skip — the button exists for the customer who
                rings up and changes their mind, not as a way past the rule. */}
            {heldUntil && (
              <Button
                variant="secondary"
                onClick={() => create.mutate(true)}
                loading={create.isPending}
                disabled={!cityId || !address.trim() || !cod}
              >
                {t('Envoyer quand même')}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('Code colis AMEEX *')} hint={t('Tel qu’affiché dans le tableau AMEEX')}>
            <input
              type="text"
              value={parcelCode}
              onChange={(e) => setParcelCode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder={t('ex. AMX0234812')}
            />
          </Field>
          <Field label={t('Ville (optionnel)')} hint={t('Tapez pour rechercher')}>
            <AmeexCitySelect
              value={cityId}
              onChange={({ id }) => setCityId(id)}
            />
          </Field>
          <div className="md:col-span-2 flex justify-end">
            <Button
              onClick={() => attach.mutate()}
              loading={attach.isPending}
              disabled={!parcelCode.trim()}
              variant="secondary"
            >
              {t('Lier le colis existant')}
            </Button>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* A failed parcel creation gets a popup, not a line of small red text —
          it is a blocking problem the operator must act on (see #D382). */}
      <AmeexErrorDialog failure={failure} onClose={() => setFailure(null)} />
    </div>
  );
}

// ─── Existing-parcel state: status, timeline, edit/cancel ───────────────────
function ExistingParcel({
  order,
  shopifyOrderId,
  onChange,
}: {
  order: Order;
  shopifyOrderId: string;
  onChange: () => void;
}) {
  const { t } = useT();
  const [editing, setEditing] = useState(false);
  const status = (order.ameex_status as AmeexCanonicalStatus | null) ?? 'pending';

  const events = useQuery({
    queryKey: ['ameex-events', shopifyOrderId],
    queryFn: () => ameexApi.events(shopifyOrderId),
  });

  const cancel = useMutation({
    mutationFn: () => ameexApi.cancelParcel(shopifyOrderId),
    onSuccess: onChange,
  });
  const refundReceived = useMutation({
    mutationFn: (received: boolean) => ameexApi.setRefundReceived(shopifyOrderId, received),
    onSuccess: onChange,
  });
  const [printBusy, setPrintBusy] = useState(false);

  return (
    <div className="space-y-4">
      {/* Latest event summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <SummaryItem label={t('Code colis')} value={order.ameex_parcel_code ?? '—'} mono />
        <SummaryItem label={t('Ville (ID)')} value={order.ameex_city_id ?? '—'} />
        <SummaryItem
          label={t('Dernier événement')}
          value={order.ameex_last_event_at ? formatDate(order.ameex_last_event_at) : '—'}
        />
        {order.ameex_status_comment && (
          <div className="md:col-span-3">
            <SummaryItem label={t('Commentaire AMEEX')} value={order.ameex_status_comment} />
          </div>
        )}
      </div>

      {/* Advance-payment proof — kept visible AFTER the parcel is created so
          the team can still see (and re-check) the deposit screenshot the
          customer sent by virement. Before this, the proof lived only in the
          create form and disappeared once the parcel existed. Only shown when
          a proof was actually uploaded, so pure-COD orders stay uncluttered. */}
      {order.advance_proof_url && (
        <AdvanceProofUploader order={order} shopifyOrderId={shopifyOrderId} onChange={onChange} />
      )}

      {/* Action bar */}
      <div className="flex gap-2 flex-wrap">
        {/* Print the AMEEX label (2026-06-10): the worker auto-saves today's
            bon de livraison then returns the print sheet — no more manual
            BL dance in the AMEEX dashboard. Opens as a blob tab (auth'd). */}
        {status !== 'cancelled' && (
          <Button
            size="sm"
            onClick={() => {
              setPrintBusy(true);
              ameexPrintApi.orderLabel(shopifyOrderId)
                .catch((e: Error) => alert(t('Étiquette indisponible : {message}', { message: e.message })))
                .finally(() => setPrintBusy(false));
            }}
            loading={printBusy}
          >
            🖨 {t('Imprimer l’étiquette')}
          </Button>
        )}
        {!editing && status !== 'cancelled' && (
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            {t('Modifier')}
          </Button>
        )}
        {status !== 'cancelled' && status !== 'delivered' && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (confirm(t('Annuler ce colis chez AMEEX ?'))) cancel.mutate();
            }}
            loading={cancel.isPending}
          >
            {t('Annuler le colis')}
          </Button>
        )}
        {(status === 'returned' || status === 'refused') && (
          <label className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-rose-200 bg-rose-50 text-rose-800">
            <input
              type="checkbox"
              checked={!!order.ameex_refund_received}
              onChange={(e) => refundReceived.mutate(e.target.checked)}
              className="rounded"
            />
            {t('Colis reçu en stock')}
          </label>
        )}
      </div>

      {editing && (
        <EditForm
          order={order}
          shopifyOrderId={shopifyOrderId}
          onDone={() => { setEditing(false); onChange(); }}
          onCancel={() => setEditing(false)}
        />
      )}

      {/* Event timeline */}
      <details className="rounded-lg border border-gray-100 bg-gray-50">
        <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-gray-600 select-none">
          {t('Historique des événements ({n})', { n: events.data?.events.length ?? 0 })}
        </summary>
        <div className="px-3 pb-3">
          {events.isLoading ? (
            <p className="text-xs text-gray-400 py-2">{t('Chargement…')}</p>
          ) : events.data?.events.length ? (
            <ul className="space-y-1.5 mt-2">
              {events.data.events.map((e) => <EventRow key={e.id} event={e} />)}
            </ul>
          ) : (
            <p className="text-xs text-gray-400 py-2">{t('Aucun événement reçu pour le moment.')}</p>
          )}
        </div>
      </details>
    </div>
  );
}

function EditForm({
  order,
  shopifyOrderId,
  onDone,
  onCancel,
}: {
  order: Order;
  shopifyOrderId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useT();
  const [cityId, setCityId] = useState(String(order.ameex_city_id ?? ''));
  const [address, setAddress] = useState(extractAddress(order.customer_note) ?? '');
  // Default to what the courier really collects — total minus what was
  // actually received, 0 when paid in full — by the same rule as the create
  // form and the worker (lib/cashToCollect.ts). ⚠️ It was « the saved acompte, else 50 »
  // (money bug 3, 2026-09-29): a client who paid nothing was still credited 50,
  // and an accepted receipt (advance_received_amount) was never read.
  const codDefault = cashToCollect({ ...order, total: Math.round(Number(order.total_price ?? 0)) });
  const [cod, setCod] = useState(String(codDefault));
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const edit = useMutation({
    mutationFn: () => ameexApi.editParcel(shopifyOrderId, {
      city_id: cityId ? Number(cityId) : undefined,
      address,
      // Send the COD only when the admin changed it from the auto default; when
      // unchanged, let the worker recompute it from the LIVE total + advance.
      cod: Number(cod) === codDefault ? undefined : Number(cod),
      comment,
    }),
    onSuccess: () => { setError(null); setLocked(false); onDone(); },
    onError: (e: Error) => { setError(e.message); setLocked(/AMEEX a refusé/.test(e.message)); },
  });
  // AMEEX locked the parcel (on a validated delivery note): a NEW parcel with
  // this address — new label — and a note to have AMEEX cancel the old one.
  const [locked, setLocked] = useState(false);
  const replace = useMutation({
    mutationFn: () => ameexApi.createParcel(shopifyOrderId, {
      city_id: cityId ? Number(cityId) : undefined,
      address_override: address,
      ...(Number(cod) === codDefault ? {} : { cod_override: Number(cod) }),
      comment,
      replace_locked: true,
      force: true,
    }),
    onSuccess: () => { setError(null); setLocked(false); onDone(); },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50">
      <Field label={t('Ville')}>
        <AmeexCitySelect
          value={cityId}
          onChange={({ id }) => setCityId(id)}
        />
      </Field>
      <Field label={t('COD (MAD)')}>
        <input type="number" value={cod} onChange={(e) => setCod(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" />
      </Field>
      <Field label={t('Adresse')} className="md:col-span-2">
        <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" />
      </Field>
      <Field label={t('Commentaire')} className="md:col-span-2">
        <input type="text" value={comment} onChange={(e) => setComment(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" />
      </Field>
      <div className="md:col-span-2 flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>{t('Annuler')}</Button>
        <Button size="sm" onClick={() => edit.mutate()} loading={edit.isPending}>{t('Enregistrer')}</Button>
      </div>
      {error && <p className="md:col-span-2 text-sm text-red-600">{error}</p>}
      {locked && (
        <div className="md:col-span-2 rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2">
          <p className="text-[13px] text-amber-900">
            {t('AMEEX a bloqué ce colis. Créez un nouveau colis avec cette adresse : nouvelle étiquette à coller sur le colis, et demandez à AMEEX d’annuler l’ancien (une note est ajoutée à la commande).')}
          </p>
          <Button size="sm" onClick={() => replace.mutate()} loading={replace.isPending}>
            {t('Créer un nouveau colis avec cette adresse')}
          </Button>
        </div>
      )}
    </div>
  );
}

function EventRow({ event }: { event: AmeexStatusEvent }) {
  const color = event.statut_color || '#9ca3af';
  return (
    <li className="flex items-start gap-2 text-xs">
      <span
        className="mt-1 inline-block w-1.5 h-1.5 rounded-full shrink-0"
        style={{ backgroundColor: color }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium text-gray-800">{event.statut_name || event.statut}</span>
          {event.statut_s && <span className="text-gray-500">· {event.statut_s}</span>}
          <span className="text-gray-400 ml-auto">{formatDate(event.received_at)}</span>
        </div>
        {event.comment && <p className="text-gray-600 mt-0.5 truncate">{event.comment}</p>}
      </div>
    </li>
  );
}

// ─── Tiny UI helpers ────────────────────────────────────────────────────────
function Field({
  label, hint, className, children,
}: { label: string; hint?: string; className?: string; children: React.ReactNode }) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-gray-400 mt-0.5">{hint}</span>}
    </label>
  );
}

function ToggleRow({
  label, value, onChange,
}: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <span
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${value ? 'bg-amber-500' : 'bg-gray-300'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </span>
      <span className="text-xs font-medium text-gray-700">{label}</span>
    </label>
  );
}

function SummaryItem({ label, value, mono }: { label: string; value: string | number; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-gray-500 mb-0.5">{label}</dt>
      <dd className={`text-sm text-gray-800 ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}

// Customer's free-text address gets folded into customer_note as
// "Adresse: <text> | …" by the COD form. Pull it out for prefill.
function extractAddress(note: string | null | undefined): string | null {
  if (!note) return null;
  // COD form: "Adresse: <text> | …". Manual orders: the raw note IS the
  // address (no prefix). Use the captured group if present, else fall back to
  // the whole note so a plainly typed address shows in the field and reaches
  // AMEEX (mirrors the worker's address extraction).
  const m = note.match(/Adresse\s*:\s*([^\n|]+)/i);
  if (m) return m[1].trim() || null;
  // ⚠️ Never a system line (#WA-168784's label read « Extra pages: 13 (104
  // MAD) »). Same rule as the worker's lib/noteAddress.ts.
  const lines = note.split(/[\n|]/).map((l) => l.trim()).filter(Boolean);
  const SYSTEM = /^\s*(extra pages|pages? (en )?plus|formule|total|tel|téléphone|mise en page|destination|relief|pack|album|pages|prix|acompte|avance|cod|livraison|commande|order|note)\b/i;
  if (!lines.length || lines.some((l) => SYSTEM.test(l) || /\d+\s*(MAD|DH)\b/i.test(l))) return null;
  return lines.join(', ');
}

// ─── Advance-payment proof uploader ─────────────────────────────────────────
// Lets the admin attach a screenshot/photo of the bank-transfer receipt
// for orders where the customer paid the 50 MAD acompte via virement.
// Shows the uploaded thumbnail + the upload timestamp so the team can
// double-check before AMEEX collects the (already-discounted) COD.
function AdvanceProofUploader({
  order,
  shopifyOrderId,
  onChange,
}: {
  order: Order;
  shopifyOrderId: string;
  onChange: () => void;
}) {
  const { t } = useT();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleFile(file: File) {
    setUploading(true);
    setErr(null);
    try {
      const ct = file.type || 'image/jpeg';
      const { upload_url, storage_path, content_type } =
        await ordersApi.advanceProofUploadUrl(shopifyOrderId, file.name, ct);
      await uploadToSignedUrl(upload_url, file, content_type);
      await ordersApi.advanceProofFinalize(shopifyOrderId, storage_path);
      onChange();
    } catch (e) {
      setErr((e as Error)?.message ?? t('Upload échoué'));
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove() {
    setUploading(true);
    try {
      await ordersApi.advanceProofDelete(shopifyOrderId);
      onChange();
    } catch (e) {
      setErr((e as Error)?.message ?? t('Suppression échouée'));
    } finally {
      setUploading(false);
    }
  }

  // PASTE-TO-UPLOAD (2026-07-14): copy a receipt screenshot (WhatsApp, bank
  // app, anywhere) and Ctrl/Cmd+V — no save-to-disk + re-upload dance. Only
  // fires when the clipboard actually carries an image; everything else pastes
  // normally. Reuses handleFile so the same signed-URL upload + finalize +
  // refresh runs.
  //
  // ⚠️ ONLY INTO THIS BOX, NEVER PAGE-WIDE (2026-09-29). It listened on the
  // whole order page, and since the same day's « 1a » change the box is on
  // every order page without a parcel. A proof upload is not a note: until
  // later that day the finalize route (worker routes/orders.ts →
  // transitionToPaid) moved the order to `paid`, stamped `paid_at`, tagged
  // Shopify, fired the one-time Purchase event (Meta, TikTok, Google) at the
  // order's FULL total — and `paid_at` then sent the client « Paiement
  // confirmé ✅ On lance l'impression. » A stray screenshot pasted anywhere on
  // the page did all of that. (Since the owner's rule of 2026-09-29 the route
  // only ATTACHES the proof — but a stray screenshot would still be filed as
  // this client's receipt.) Now the paste is the box's only while the box has focus (click it,
  // then paste) or the pointer is over it; a paste aimed at a text field
  // stays a text paste. Guard: AmeexDeliveryCard.proofPaste.test.tsx.
  const handleFileRef = useRef(handleFile);
  handleFileRef.current = handleFile;
  const boxRef = useRef<HTMLDivElement | null>(null);
  const hoveredRef = useRef(false);
  useEffect(() => {
    const box = boxRef.current;
    const enter = () => { hoveredRef.current = true; };
    const leave = () => { hoveredRef.current = false; };
    box?.addEventListener('mouseenter', enter);
    box?.addEventListener('mouseleave', leave);
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      const focusedInBox = Boolean(box && target && box.contains(target));
      if (!focusedInBox) {
        // Not the box's paste unless the pointer is over it — and a paste into
        // a text field elsewhere is that field's, pointer or not.
        if (!hoveredRef.current) return;
        if (target?.closest?.('input, textarea, [contenteditable="true"]')) return;
      }
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of Array.from(items)) {
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) {
            e.preventDefault();
            const ext = it.type.split('/')[1] || 'png';
            void handleFileRef.current(
              new File([f], `preuve-collee-${Date.now()}.${ext}`, { type: it.type }),
            );
          }
          return;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('paste', onPaste);
      box?.removeEventListener('mouseenter', enter);
      box?.removeEventListener('mouseleave', leave);
    };
  }, []);

  const hasProof = !!order.advance_proof_url;

  return (
    // Focusable, so a click on the frame gives it the paste (see above).
    <div
      ref={boxRef}
      tabIndex={0}
      aria-label={t('Preuve de l’acompte')}
      className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2 outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-300 hover:border-gray-200"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold text-gray-700">{t('Preuve de l’acompte')}</p>
          <p className="text-[11px] text-gray-500">
            {hasProof
              ? t('Reçu téléversé le {date}', { date: formatDate(order.advance_proof_uploaded_at ?? '') })
              : t('Joignez un reçu / capture du virement — ou cliquez dans ce cadre puis collez l’image (Ctrl+V / Cmd+V).')}
          </p>
          {/* Owner, 2026-09-29: a proof ATTACHES, nothing more. Until that day
              the upload moved the order to « Payé » by itself; the box says
              it no longer does, so nobody waits for a badge that won't come. */}
          <p className="text-[11px] text-gray-500">
            {t('Pièce jointe seulement : téléverser une preuve ne confirme pas la commande et ne la marque pas payée.')}
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
          />
          <Button size="sm" variant="secondary" loading={uploading} onClick={() => fileRef.current?.click()}>
            {hasProof ? t('Remplacer') : t('Téléverser')}
          </Button>
          {hasProof && (
            <Button size="sm" variant="ghost" onClick={handleRemove} disabled={uploading}>
              {t('Retirer')}
            </Button>
          )}
        </div>
      </div>

      {hasProof && order.advance_proof_url && (
        <a
          href={order.advance_proof_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full max-w-[180px] rounded-md border border-gray-200 overflow-hidden bg-white hover:border-brand-300 transition-colors"
          title={t('Ouvrir la preuve dans un nouvel onglet')}
          data-adm-media
        >
          <img
            src={order.advance_proof_url}
            alt={t('Preuve de l’acompte')}
            className="block w-full h-auto"
            loading="lazy"
            onError={(e) => {
              // If the file is a PDF the <img> will fail to load — replace
              // with a generic icon block so the link is still clickable.
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        </a>
      )}

      {err && <p className="text-[11px] text-red-600">{err}</p>}
    </div>
  );
}
